/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages } from 'react-intl-next';

import type { UniversalCreateSourceProduct } from '@atlassian/universal-create-common';

import type { SuggestionConfigContext } from '../../../../../types';
import { SuggestionItemBase, type SuggestionItemBaseProps } from '../../SuggestionItemBase';

import { useLoomVideoConfig } from './hooks/use-loom-video-config';
import { convertSourceProductToProductType } from './utils';

export interface LoomVideoConfigProps extends SuggestionItemBaseProps {
	context: SuggestionConfigContext;
	product: UniversalCreateSourceProduct;
}

// TODO: eventually remove after BE translation completed
export const messages = defineMessages({
	loomVideo: {
		id: 'universal-create.loom-video-menu-item.create-loom-video',
		defaultMessage: 'Loom video',
		description: 'Loom video menu item in the create menu',
	},
});

/**
 * Higher-order component that wraps a component with Loom video configuration
 * @param WrappedComponent - The component to wrap with Loom video configuration
 * @returns A new component with Loom video configuration applied
 */
function withLoomVideoConfig<P extends LoomVideoConfigProps>(
	WrappedComponent: ComponentType<P>,
): ComponentType<LoomVideoConfigProps> {
	return function WithLoomVideoConfigComponent(props: LoomVideoConfigProps) {
		const { context, product } = props;

		const customisedItemConfig = useLoomVideoConfig({
			cloudId: context.cloudId,
			productName: convertSourceProductToProductType(product),
			environment: context.environment || 'prod',
			onItemClick: context.onItemClick,
		});

		const finalItemConfig = {
			...customisedItemConfig,
		};

		return <WrappedComponent {...(props as P)} customisedItemConfig={finalItemConfig} />;
	};
}

/**
 * LoomVideoMenuItem component that renders a Loom video suggestion item
 * Uses the withLoomVideoConfig HOC to apply Loom-specific configuration
 */
export const LoomVideoItem = withLoomVideoConfig(SuggestionItemBase);
